import type { FilterCondition, User, UserRole } from "../../api/types";
import { toCsv } from "../../lib/csv";

export const roleLabels: Record<UserRole, string> = {
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
  guest: "Guest",
};

export const roleOptions: { value: UserRole; label: string }[] = (
  ["admin", "member", "viewer", "guest"] as const
).map((value) => ({ value, label: roleLabels[value] }));

export type MemberTab =
  "all" | "admins" | "members" | "viewers" | "guests" | "suspended" | "invited";

/**
 * The tabs are structured filters, not the deprecated `filter` scalar
 * (`docs/spec/api-conventions.md` §7; `docs/spec/users-and-teams.md` § API
 * surface). "Invited" is `lastActiveAt isNull`, which is what marks an account
 * that has never been signed in to.
 */
export const memberTabs: { id: MemberTab; label: string; filters: FilterCondition[] }[] = [
  { id: "all", label: "All", filters: [] },
  { id: "admins", label: "Admins", filters: [{ field: "role", operator: "eq", value: "admin" }] },
  {
    id: "members",
    label: "Members",
    filters: [{ field: "role", operator: "eq", value: "member" }],
  },
  {
    id: "viewers",
    label: "Viewers",
    filters: [{ field: "role", operator: "eq", value: "viewer" }],
  },
  { id: "guests", label: "Guests", filters: [{ field: "role", operator: "eq", value: "guest" }] },
  {
    id: "suspended",
    label: "Suspended",
    filters: [{ field: "suspendedAt", operator: "isNotNull" }],
  },
  { id: "invited", label: "Invited", filters: [{ field: "lastActiveAt", operator: "isNull" }] },
];

export type MemberStatus = "Suspended" | "Invited" | "Active";

export function userStatus(user: User): MemberStatus {
  if (user.isSuspended === true) return "Suspended";
  if (user.lastActiveAt === null || user.lastActiveAt === undefined) return "Invited";
  return "Active";
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Relative for the recent past, an absolute ISO date beyond a week. Spelled out
 * here rather than taken from a date library: it is fifteen lines and it keeps
 * the rendered string stable across locales, which matters for the tests.
 */
export function formatLastActive(value: string | null | undefined, now = new Date()): string {
  if (value === null || value === undefined || value === "") return "Never";
  const then = new Date(value);
  const elapsed = now.getTime() - then.getTime();
  if (Number.isNaN(elapsed)) return "Never";
  if (elapsed < MINUTE) return "Just now";
  if (elapsed < HOUR) {
    const minutes = Math.floor(elapsed / MINUTE);
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  if (elapsed < DAY) {
    const hours = Math.floor(elapsed / HOUR);
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  if (elapsed < 7 * DAY) {
    const days = Math.floor(elapsed / DAY);
    return days === 1 ? "Yesterday" : `${days} days ago`;
  }
  return then.toISOString().slice(0, 10);
}

/** The CSV is the list as displayed: same rows, same order, same columns. */
export function membersCsv(users: readonly User[]): string {
  return toCsv([
    ["Name", "Email", "Role", "Status", "Last active"],
    ...users.map((user) => [
      user.name,
      user.email ?? "",
      user.role === undefined ? "" : roleLabels[user.role],
      userStatus(user),
      user.lastActiveAt ?? "",
    ]),
  ]);
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

/** `docs/spec/users-and-teams.md` §6: at most 20 addresses in one call. */
export const MAX_INVITES = 20;
